<template>
  <Teleport to="body">
    <div
      ref="popupEl"
      class="map-kit-overpass-popup"
      :class="popupPlacementClass"
      v-show="mapViewMode === 'aoi' && selectedAoiPass && popupVisible"
      :style="popupStyle"
    >
      <button class="map-kit-overpass-popup-close" @click="closePopup">&times;</button>
      <div v-if="selectedAoiPass" class="map-kit-overpass-popup-body">
        <strong>{{ selectedAoiPassDisplayName }}</strong>
        <table class="map-kit-overpass-popup-table">
          <tbody>
            <tr><td>{{ $t('popup.start') }}</td><td>{{ formatDateTime(selectedAoiPass.start_time) }}</td></tr>
            <tr><td>{{ $t('popup.end') }}</td><td>{{ formatDateTime(selectedAoiPass.end_time) }}</td></tr>
            <tr v-if="selectedAoiPass.direction"><td>{{ $t('popup.direction') }}</td><td>{{ selectedAoiPass.direction === 'ASCENDING' ? $t('popup.ascending') : $t('popup.descending') }}</td></tr>
            <tr v-if="selectedAoiPass.sensor_type"><td>{{ $t('popup.sensor') }}</td><td>{{ selectedAoiPass.sensor_type }}</td></tr>
            <tr v-if="selectedAoiPass.spatial_res_m != null"><td>{{ $t('popup.resolution') }}</td><td>{{ selectedAoiPass.spatial_res_m }}m</td></tr>
            <tr v-if="selectedAoiPass.constellation"><td>{{ $t('popup.constellation') }}</td><td>{{ selectedAoiPass.constellation }}</td></tr>
            <tr v-if="selectedAoiPass.is_daytime != null"><td>{{ $t('popup.daylight') }}</td><td>{{ selectedAoiPass.is_daytime ? $t('popup.dayEmoji') : $t('popup.nightEmoji') }}</td></tr>
            <tr>
              <td>{{ selectedAoiPassIsPast ? $t('popup.cloudCoverObserved') : $t('popup.cloudCoverForecast') }}</td>
              <td>{{ selectedAoiPass.cloud_cover_pct != null ? `${selectedAoiPass.cloud_cover_pct}%` : $t('popup.notCalculated') }}</td>
            </tr>
            <tr v-if="selectedAoiCoveredPct != null">
              <td>{{ $t('popup.areaCovered') }}</td>
              <td>{{ selectedAoiCoveredPct }}%</td>
            </tr>
            <tr v-if="selectedAoiPassLatency != null"><td>{{ $t('popup.expectedLatency') }}</td><td>~{{ Math.round(selectedAoiPassLatency) }} min</td></tr>
            <tr v-if="selectedAoiPassDelivery"><td>{{ $t('popup.expectedDelivery') }}</td><td>{{ formatDateTime(selectedAoiPassDelivery) }}</td></tr>
          </tbody>
        </table>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { watch, onBeforeUnmount, computed, ref, nextTick, useTemplateRef } from 'vue';
import GeoJSON from 'ol/format/GeoJSON';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { Fill, Stroke, Style } from 'ol/style';
import { createEmpty, extend as extendExtent, isEmpty as isEmptyExtent } from 'ol/extent';
import type Feature from 'ol/Feature';
import type Geometry from 'ol/geom/Geometry';
import type { MapBrowserEvent } from 'ol';

import { useMap } from '@/composables/useMap';
import { useAoiStore, resolveSatelliteName } from '@/composables/useAoiStore';
import {
  collapseOroraConstellationPasses,
  getSatelliteDisplayName,
  groupedPassContainsRawId,
  isOroraTechConstellationSatellite,
} from '@/composables/useConstellationGrouping';
import { useMonitoredAreasStore } from '@/composables/useMonitoredAreasStore';
import { useFilterStore } from '@/composables/useFilterStore';
import { parseUtcEpochMs, useDateTimeSettings } from '@/composables/useDateTimeSettings';
import { getTimingColorForHours, getTimingOrderRank, NO_UPCOMING_HOURS } from '@/composables/useTimingColorScale';
import { isColorblindFriendly } from '@/composables/useAccessibilitySettings';
import {
  useSatelliteLatency,
  computeExpectedDeliveryForPass,
  getExpectedLatencyMinutesForPass,
} from '@/composables/useSatelliteLatency';

const { map, fitExtent } = useMap();
const { coverageResult } = useAoiStore();
const { areas, visibleAreas, aoiPassesAll, aoiPasses } = useMonitoredAreasStore();
useSatelliteLatency();
const {
  selectedSatelliteNames,
  timeRange,
  metaTimeRange,
  mapViewMode,
  aoiCoverageVisible,
  aoiCoverageOpacity,
  selectedPassId,
  selectedPassSourceIds,
  hoveredPassSourceIds,
  coverageHoverActive,
  setSelectedPass,
  setHoveredPass,
  selectedPass,
  swathGraphPasses,
  hoveredPassId,
  ororaConstellationExpanded,
  otDataSourceFilter,
  cloudCoverThreshold,
  standardForecastEndMs,
  passWithinForecastHorizon,
} = useFilterStore();
const { formatDateTime } = useDateTimeSettings();

const format = new GeoJSON();
const popupEl = useTemplateRef('popupEl');

const activeAoiSelectedId = computed<string | null>(() => {
  const id = selectedPassId.value;
  if (!id || !id.startsWith('aoi-')) return null;
  return id;
});

const selectedAoiPass = computed(() => {
  if (!activeAoiSelectedId.value) return null;
  return aoiPassesAll.value.find((p) => p.id === activeAoiSelectedId.value) ?? null;
});
const selectedAoiPassDisplayName = computed(() => {
  const pass = selectedAoiPass.value;
  if (!pass) return '';
  return getSatelliteDisplayName(pass.name, ororaConstellationExpanded.value);
});
/** A pass that has already happened reports cloud that was observed, not forecast —
 *  and for a past observation that is the number that decides whether the imagery was
 *  usable at all, so it is labelled honestly rather than hidden. */
const selectedAoiPassIsPast = computed(() => {
  const endMs = Date.parse(selectedAoiPass.value?.end_time ?? '');
  return Number.isFinite(endMs) && endMs < Date.now();
});
/** How much of the area this pass actually observed — a 5% clip and full coverage
 *  are otherwise indistinguishable in the list. */
const selectedAoiCoveredPct = computed(() => {
  const fraction = selectedAoiPass.value?.covered_fraction;
  if (fraction == null || !Number.isFinite(fraction)) return null;
  return Math.round(fraction * 100);
});
const selectedAoiPassLatency = computed(() => {
  const pass = selectedAoiPass.value;
  if (!pass) return null;
  return getExpectedLatencyMinutesForPass(pass);
});
const selectedAoiPassDelivery = computed(() => {
  const pass = selectedAoiPass.value;
  if (!pass) return null;
  return computeExpectedDeliveryForPass(pass);
});
const selectedAoiSourceIdSet = computed(() =>
  new Set(selectedPassSourceIds.value.filter((id) => id.startsWith('aoi-'))),
);
const hoveredAoiSourceIdSet = computed(() =>
  new Set(hoveredPassSourceIds.value.filter((id) => id.startsWith('aoi-'))),
);
const groupedAoiPasses = computed(() =>
  collapseOroraConstellationPasses(aoiPasses.value, ororaConstellationExpanded.value, {
    timeBucketReferenceMs: timeRange.value[0],
  }),
);
const aoiPassClippedWindowById = computed(() => {
  const byId = new Map<string, [number, number]>();
  for (const pass of aoiPassesAll.value) {
    const startMs = parseUtcEpochMs(pass.start_time);
    const endMs = parseUtcEpochMs(pass.end_time);
    if (Number.isFinite(startMs) && Number.isFinite(endMs)) {
      byId.set(pass.id, [startMs, endMs]);
    }
  }
  return byId;
});
const selectedAoiContextIdSet = computed(() => {
  const selectedId = activeAoiSelectedId.value;
  if (!selectedId) return new Set<string>();
  const groupedPass = groupedAoiPasses.value.find((pass) => groupedPassContainsRawId(pass, selectedId));
  if (!groupedPass || groupedPass.sourcePassIds.length === 0) return new Set([selectedId]);
  const contextIds = groupedPass.sourcePassIds.filter((id) => id.startsWith('aoi-'));
  return new Set(contextIds.length ? contextIds : [selectedId]);
});

function getGroupedAoiHoverSourcePassIds(rawPassId: string): string[] {
  const groupedPass = groupedAoiPasses.value.find((pass) => groupedPassContainsRawId(pass, rawPassId));
  if (!groupedPass || groupedPass.sourcePassIds.length === 0) return [rawPassId];
  return groupedPass.sourcePassIds;
}

const popupVisible = ref(false);
const popupStyle = ref<Record<string, string>>({});
const popupPlacementClass = ref('popup--bottom');
const popupDismissedForPassId = ref<string | null>(null);
let lastClickCoord: number[] | null = null;

const POPUP_MARGIN = 12;
const ARROW_SIZE = 6;
const coverageFeatureMap = new Map<string, { center: number[]; extent: [number, number, number, number] }>();
let lastAutoFocusKey: string | null = null;
const COVERAGE_SORT_MS_KEY = '__timingSortMs';
const COVERAGE_HOURS_KEY = '__timingHours';
const coverageStyleCache = new Map<string, Style>();

/* AOI outline layer */
const aoiSource = new VectorSource();
const aoiLayer = new VectorLayer({
  source: aoiSource,
  style: new Style({
    fill: new Fill({ color: 'rgba(59, 130, 246, 0.08)' }),
    stroke: new Stroke({ color: '#3b82f6', width: 2, lineDash: [6, 6] }),
  }),
  zIndex: 100, // Should sit above Grid (1) and Swaths (60/61)
});

const coverageSource = new VectorSource();
const coverageLayer = new VectorLayer({
  source: coverageSource,
  opacity: aoiCoverageOpacity.value,
  style: (feature) => {
    const props = feature.getProperties() as Record<string, unknown>;
    const hoursRaw = Number(feature.get(COVERAGE_HOURS_KEY));
    const hours = Number.isFinite(hoursRaw) ? hoursRaw : getCoverageHoursToPass(props);
    const selectedIds = selectedAoiSourceIdSet.value;
    const selectedContextIds = selectedAoiContextIdSet.value;
    const hasSelected = selectedIds.size > 0;

    if (hasSelected) {
      const passId = buildCoveragePassId(props);
      if (passId && selectedIds.has(passId)) {
        return getCoverageStyle(hours, 'base');
      }
      if (passId && selectedContextIds.has(passId)) {
        return getCoverageStyle(hours, 'base');
      }
      return getCoverageStyle(hours, 'dimmed');
    }

    // Coverage graph hover: dim ALL base-layer features (both hovered and
    // non-hovered) so the highlight layer's opaque teal is the only visible
    // fill — no timing color bleeds through the transparency.
    if (coverageHoverActive.value) {
      return getCoverageStyle(hours, 'dimmed');
    }

    return getCoverageStyle(hours, 'base');
  },
  zIndex: 51,
});

// Separate highlight layer renders hovered features on top without
// affecting the hit-test order of the base coverageLayer.
const coverageHighlightLayer = new VectorLayer({
  source: coverageSource,
  opacity: aoiCoverageOpacity.value,
  style: (feature) => {
    const props = feature.getProperties() as Record<string, unknown>;
    const passId = buildCoveragePassId(props);
    if (!passId) return undefined;
    const isSelected = selectedAoiSourceIdSet.value.has(passId);
    const isHovered = hoveredAoiSourceIdSet.value.has(passId);
    if (!isSelected && !isHovered) return undefined;
    if (isSelected) {
      const hoursRaw = Number(feature.get(COVERAGE_HOURS_KEY));
      const hours = Number.isFinite(hoursRaw) ? hoursRaw : getCoverageHoursToPass(props);
      return getCoverageStyle(hours, 'selected');
    }
    // Coverage graph hover uses teal; direct map hover uses timing colors
    if (coverageHoverActive.value) {
      return getCoverageStyle(0, 'coverage-hover');
    }
    const hoursRaw = Number(feature.get(COVERAGE_HOURS_KEY));
    const hours = Number.isFinite(hoursRaw) ? hoursRaw : getCoverageHoursToPass(props);
    return getCoverageStyle(hours, 'hover');
  },
  zIndex: 52,
});

function buildCoveragePassId(props: Record<string, unknown>): string | null {
  const satName = resolveSatelliteName(props);
  if (!satName) return null;
  const startStr = String(props.start_time ?? props.startTime ?? props.start ?? '');
  const endStr = String(props.end_time ?? props.endTime ?? props.end ?? '');
  if (!startStr || !endStr) return null;
  return `aoi-${satName}-${startStr}-${endStr}`;
}

function getCoverageStartMs(props: Record<string, unknown>): number {
  return parseUtcEpochMs(String(props.start_time ?? props.startTime ?? props.start ?? ''));
}

function getCoverageHoursToPass(props: Record<string, unknown>): number {
  const startMs = getCoverageStartMs(props);
  if (!Number.isFinite(startMs)) return NO_UPCOMING_HOURS;
  return Math.max(0, (startMs - timeRange.value[0]) / 3_600_000);
}

function annotateCoverageFeature(feature: Feature<Geometry>) {
  const props = feature.getProperties() as Record<string, unknown>;
  const startMs = getCoverageStartMs(props);
  const sortMs = Number.isFinite(startMs) ? startMs : Number.POSITIVE_INFINITY;
  const hours = Number.isFinite(startMs)
    ? Math.max(0, (startMs - timeRange.value[0]) / 3_600_000)
    : NO_UPCOMING_HOURS;
  feature.set(COVERAGE_SORT_MS_KEY, sortMs, true);
  feature.set(COVERAGE_HOURS_KEY, hours, true);
}

function getCoverageSortMs(feature: Feature<Geometry>): number {
  const value = Number(feature.get(COVERAGE_SORT_MS_KEY));
  return Number.isFinite(value) ? value : Number.POSITIVE_INFINITY;
}

function getCoverageStyle(hours: number, state: 'base' | 'hover' | 'selected' | 'dimmed' | 'coverage-hover'): Style {
  const isCoverageHover = state === 'coverage-hover';
  const fillAlpha = state === 'dimmed' ? 0 : 1;
  const unavailableAlpha = state === 'dimmed' ? 0 : 1;
  const fillColor = isCoverageHover
    ? 'rgba(3, 222, 204, 0.6)'
    : getTimingColorForHours(hours, fillAlpha, unavailableAlpha);
  const rank = getTimingOrderRank(hours);
  const key = `${state}-${fillColor}-${rank}`;
  let style = coverageStyleCache.get(key);
  if (!style) {
    const stroke = isCoverageHover
      ? new Stroke({ color: 'rgba(3, 222, 204, 0.8)', width: 1.5 })
      : new Stroke({ color: 'rgba(12, 24, 32, 0.0)', width: 0.0 });
    let zIndex = rank;
    if (state === 'hover' || isCoverageHover) {
      zIndex = 1200 + rank;
    } else if (state === 'selected') {
      zIndex = 1300 + rank;
    }
    style = new Style({
      fill: new Fill({ color: fillColor }),
      stroke,
      zIndex,
    });
    coverageStyleCache.set(key, style);
  }
  return style;
}

function filterFeaturesBySatelliteAndTime(features: Feature<Geometry>[]): Feature<Geometry>[] {
  const selectedUpper = new Set(selectedSatelliteNames.value.map((n) => n.toUpperCase()));
  if (selectedUpper.size === 0) return [];

  const [rangeStart, rangeEnd] = timeRange.value;
  const expectedSource = otDataSourceFilter.value === 'predicted' ? 'api' : 'forest_client';
  const maxCloud = cloudCoverThreshold.value;

  return features.filter((f) => {
    const props = f.getProperties();
    const satName = resolveSatelliteName(props as Record<string, unknown>);
    if (!satName) return false;
    if (!selectedUpper.has(satName.toUpperCase())) return false;

    // OroraTech data source filter
    if (isOroraTechConstellationSatellite(satName)) {
      const dataSource = String((props as Record<string, unknown>).data_source ?? '');
      if (dataSource && dataSource !== expectedSource) return false;
    }

    const startStr = props.start_time ?? props.startTime ?? props.start;
    const endStr = props.end_time ?? props.endTime ?? props.end;
    const startMs = parseUtcEpochMs(String(startStr ?? ''));
    const endMs = parseUtcEpochMs(String(endStr ?? ''));
    const dataSource = String((props as Record<string, unknown>).data_source ?? '');
    if (!passWithinForecastHorizon(satName, startMs, dataSource)) return false;
    const hasCloudForecast = dataSource === 'api' || startMs <= standardForecastEndMs.value;
    if (!hasCloudForecast) {
      f.set('cloud_cover_pct', null);
    }

    // Cloud cover threshold filter
    if (maxCloud < 100 && hasCloudForecast) {
      const rawCloud = (props as Record<string, unknown>).cloud_cover_pct;
      if (rawCloud != null) {
        const cloudPct = Number(rawCloud);
        if (Number.isFinite(cloudPct) && cloudPct > maxCloud) return false;
      }
    }

    // Prefer clipped AOI pass window (same timestamps used by timeline graph).
    const passId = buildCoveragePassId(props as Record<string, unknown>);
    if (passId) {
      const clipped = aoiPassClippedWindowById.value.get(passId);
      if (clipped) {
        return clipped[1] >= rangeStart && clipped[0] <= rangeEnd;
      }
    }

    // Fallback to raw feature interval if we cannot resolve clipped AOI timings.
    if (Number.isFinite(startMs) && Number.isFinite(endMs)) {
      return endMs >= rangeStart && startMs <= rangeEnd;
    }

    // Unknown time bounds are excluded from time-filtered map view.
    return false;
  });
}

function onCoverageClick(evt: MapBrowserEvent<PointerEvent>) {
  if (mapViewMode.value !== 'aoi') return;
  const m = map.value;
  if (!m) return;

  const currentSelectedIds = selectedAoiContextIdSet.value;
  const currentSelectedSourceIds = selectedAoiSourceIdSet.value;
  let foundId: string | null = null;
  m.forEachFeatureAtPixel(
    evt.pixel,
    (feature) => {
      const props = feature.getProperties();
      const passId = buildCoveragePassId(props);
      if (!passId) return;
      // When a pass is selected, dimmed features are invisible — skip them for click
      if (currentSelectedIds.size > 0 && !currentSelectedIds.has(passId)) return;
      foundId = passId;
      return true;
    },
    { layerFilter: (l) => l === coverageLayer || l === coverageHighlightLayer },
  );

  if (foundId && currentSelectedSourceIds.has(foundId)) {
    // Deselect (clicking the selected pass, or clicking empty space when selected)
    setSelectedPass(null);
    lastClickCoord = null;
  } else if (foundId) {
    setSelectedPass(foundId);
    lastClickCoord = evt.coordinate;
  } else {
    setSelectedPass(null);
    lastClickCoord = null;
  }
}

function onCoverageHover(evt: MapBrowserEvent<PointerEvent>) {
  if (mapViewMode.value !== 'aoi') return;
  const m = map.value;
  if (!m) return;

  const currentSelectedIds = selectedAoiContextIdSet.value;
  let foundId: string | null = null;
  m.forEachFeatureAtPixel(
    evt.pixel,
    (feature) => {
      const props = feature.getProperties();
      const passId = buildCoveragePassId(props);
      if (!passId) return;
      // When a pass is selected, dimmed features are invisible — skip them for hover
      if (currentSelectedIds.size > 0 && !currentSelectedIds.has(passId)) return;
      foundId = passId;
      return true;
    },
    // Hover should track only the top feature in the base coverage layer.
    // Including coverageHighlightLayer causes lifted features to mask underlying top hits
    // and makes hover feel "stuck" on large polygons.
    { layerFilter: (l) => l === coverageLayer },
  );
  const hoverSourceIds = !foundId
    ? []
    : currentSelectedIds.size > 0
      ? [foundId]
      : getGroupedAoiHoverSourcePassIds(foundId);
  setHoveredPass(foundId, hoverSourceIds);
}

function onViewportLeave() {
  setHoveredPass(null);
}

function findVisibleAnchorPixel(
  center: number[],
  extent: [number, number, number, number],
  m: any,
  vpWidth: number,
  vpHeight: number,
): [number, number] | null {
  const centerPx = m.getPixelFromCoordinate(center);
  if (centerPx &&
      centerPx[0] >= 0 && centerPx[0] <= vpWidth &&
      centerPx[1] >= 0 && centerPx[1] <= vpHeight) {
    return centerPx;
  }

  const topLeft = m.getPixelFromCoordinate([extent[0], extent[3]]);
  const bottomRight = m.getPixelFromCoordinate([extent[2], extent[1]]);
  if (!topLeft || !bottomRight) return null;

  const fMinX = Math.min(topLeft[0], bottomRight[0]);
  const fMaxX = Math.max(topLeft[0], bottomRight[0]);
  const fMinY = Math.min(topLeft[1], bottomRight[1]);
  const fMaxY = Math.max(topLeft[1], bottomRight[1]);

  const iMinX = Math.max(0, fMinX);
  const iMaxX = Math.min(vpWidth, fMaxX);
  const iMinY = Math.max(0, fMinY);
  const iMaxY = Math.min(vpHeight, fMaxY);

  if (iMinX >= iMaxX || iMinY >= iMaxY) return null;
  return [(iMinX + iMaxX) / 2, (iMinY + iMaxY) / 2];
}

function placePopupAtPixel(anchorMapX: number, anchorMapY: number, vpRect: DOMRect) {
  const screenX = vpRect.left + anchorMapX;
  const screenY = vpRect.top + anchorMapY;

  popupVisible.value = true;
  popupStyle.value = { left: '-9999px', top: '-9999px' };

  nextTick(() => {
    if (!popupEl.value) return;
    const popupRect = popupEl.value.getBoundingClientRect();
    const pw = popupRect.width;
    const ph = popupRect.height;
    const gap = ARROW_SIZE + 8;

    const spaceAbove = anchorMapY;
    const spaceBelow = vpRect.height - anchorMapY;
    const spaceLeft = anchorMapX;
    const spaceRight = vpRect.width - anchorMapX;

    type Placement = 'bottom' | 'top' | 'right' | 'left';
    let placement: Placement = 'bottom';
    if (spaceAbove >= ph + gap + POPUP_MARGIN) placement = 'bottom';
    else if (spaceBelow >= ph + gap + POPUP_MARGIN) placement = 'top';
    else if (spaceRight >= pw + gap + POPUP_MARGIN) placement = 'left';
    else if (spaceLeft >= pw + gap + POPUP_MARGIN) placement = 'right';

    let left: number;
    let top: number;
    if (placement === 'bottom') {
      left = screenX - pw / 2;
      top = screenY - ph - gap;
    } else if (placement === 'top') {
      left = screenX - pw / 2;
      top = screenY + gap;
    } else if (placement === 'left') {
      left = screenX + gap;
      top = screenY - ph / 2;
    } else {
      left = screenX - pw - gap;
      top = screenY - ph / 2;
    }

    left = Math.max(vpRect.left + POPUP_MARGIN, Math.min(vpRect.right - pw - POPUP_MARGIN, left));
    top = Math.max(vpRect.top + POPUP_MARGIN, Math.min(vpRect.bottom - ph - POPUP_MARGIN, top));

    popupPlacementClass.value = `popup--${placement}`;
    popupStyle.value = {
      position: 'fixed',
      left: `${Math.round(left)}px`,
      top: `${Math.round(top)}px`,
      zIndex: '10000',
    };
  });
}

function positionPopup(coord: number[]) {
  const m = map.value;
  if (!m || !popupEl.value) { popupVisible.value = false; return; }
  const pixel = m.getPixelFromCoordinate(coord);
  if (!pixel) { popupVisible.value = false; return; }
  const viewport = m.getViewport();
  if (!viewport) { popupVisible.value = false; return; }
  const vpRect = viewport.getBoundingClientRect();
  const anchorX = Math.max(0, Math.min(vpRect.width, pixel[0]));
  const anchorY = Math.max(0, Math.min(vpRect.height, pixel[1]));
  placePopupAtPixel(anchorX, anchorY, vpRect);
}

function positionPopupFromExtent(center: number[], extent: [number, number, number, number]) {
  const m = map.value;
  if (!m || !popupEl.value) { popupVisible.value = false; return; }
  const viewport = m.getViewport();
  if (!viewport) { popupVisible.value = false; return; }
  const vpRect = viewport.getBoundingClientRect();
  const anchor = findVisibleAnchorPixel(center, extent, m, vpRect.width, vpRect.height);
  if (!anchor) {
    popupVisible.value = false;
    return;
  }
  placePopupAtPixel(anchor[0], anchor[1], vpRect);
}

function closePopup() {
  popupVisible.value = false;
  popupDismissedForPassId.value = activeAoiSelectedId.value ?? null;
}

function onMapMoveEnd() {
  if (mapViewMode.value !== 'aoi' || !activeAoiSelectedId.value) return;
  if (popupDismissedForPassId.value === activeAoiSelectedId.value) return;
  const entry = coverageFeatureMap.get(activeAoiSelectedId.value);
  if (entry) {
    positionPopupFromExtent(entry.center, entry.extent);
  } else if (lastClickCoord) {
    positionPopup(lastClickCoord);
  }
}

function registerCoverageFeature(feature: Feature<Geometry>) {
  const props = feature.getProperties();
  const passId = buildCoveragePassId(props);
  if (!passId) return;
  const geom = feature.getGeometry();
  if (!geom) return;
  const ext = geom.getExtent();
  if (!Number.isFinite(ext[0])) return;
  coverageFeatureMap.set(passId, {
    center: [(ext[0] + ext[2]) / 2, (ext[1] + ext[3]) / 2],
    extent: [ext[0], ext[1], ext[2], ext[3]],
  });
}

function parseTimeMs(value: string | null | undefined): number {
  if (!value) return Number.NEGATIVE_INFINITY;
  const ms = parseUtcEpochMs(value);
  return Number.isFinite(ms) ? ms : Number.NEGATIVE_INFINITY;
}

function getMostRecentCalculatedArea() {
  const withCoverage = areas.value.filter((a) => !!a.coverage_geojson);
  if (!withCoverage.length) return null;
  return withCoverage.reduce((best, area) => {
    if (!best) return area;
    const areaTs = parseTimeMs(area.queried_at) !== Number.NEGATIVE_INFINITY
      ? parseTimeMs(area.queried_at)
      : parseTimeMs(area.modified_at);
    const bestTs = parseTimeMs(best.queried_at) !== Number.NEGATIVE_INFINITY
      ? parseTimeMs(best.queried_at)
      : parseTimeMs(best.modified_at);
    return areaTs > bestTs ? area : best;
  }, null as (typeof withCoverage[number] | null));
}

function getCoverageExtent(coverageGeojson: Record<string, unknown>): [number, number, number, number] | null {
  try {
    const features = format.readFeatures(coverageGeojson, { featureProjection: 'EPSG:3857' });
    if (!features.length) return null;
    const ext = createEmpty();
    for (const f of features) {
      const geom = f.getGeometry();
      if (!geom) continue;
      extendExtent(ext, geom.getExtent());
    }
    if (isEmptyExtent(ext) || !Number.isFinite(ext[0])) return null;
    return [ext[0], ext[1], ext[2], ext[3]];
  } catch {
    return null;
  }
}

function focusMostRecentCalculatedAoi(force = false): void {
  if (mapViewMode.value !== 'aoi') return;
  const area = getMostRecentCalculatedArea();
  if (!area || !area.coverage_geojson) return;
  const key = `${area.id}:${area.queried_at ?? area.modified_at ?? ''}`;
  if (!force && lastAutoFocusKey === key) return;
  const ext = getCoverageExtent(area.coverage_geojson);
  if (!ext) return;
  fitExtent(ext, [80, 80, 80, 80]);
  lastAutoFocusKey = key;
}

watch([selectedPassId, selectedPassSourceIds], () => {
  coverageLayer.changed();
}, { deep: true });

watch([hoveredPassId, hoveredPassSourceIds, coverageHoverActive], () => {
  coverageLayer.changed();
  coverageHighlightLayer.changed();
}, { deep: true });

watch(aoiCoverageOpacity, (opacity) => {
  coverageLayer.setOpacity(opacity);
  coverageHighlightLayer.setOpacity(opacity);
}, { immediate: true });

watch(activeAoiSelectedId, (id) => {
  if (id && id !== popupDismissedForPassId.value) {
    popupDismissedForPassId.value = null;
  }
  if (!id || mapViewMode.value !== 'aoi') {
    popupVisible.value = false;
    if (!id) popupDismissedForPassId.value = null;
    return;
  }
  if (popupDismissedForPassId.value === id) {
    popupVisible.value = false;
    return;
  }
  if (lastClickCoord) {
    const coord = lastClickCoord;
    lastClickCoord = null;
    nextTick(() => positionPopup(coord));
    return;
  }
  const entry = coverageFeatureMap.get(id);
  if (!entry) {
    popupVisible.value = false;
    return;
  }
  nextTick(() => positionPopupFromExtent(entry.center, entry.extent));
});

watch(activeAoiSelectedId, (id) => {
  if (!id || mapViewMode.value !== 'aoi') return;
  const m = map.value;
  if (!m) return;
  const features = coverageSource.getFeatures();
  for (const f of features) {
    const props = f.getProperties();
    const passId = buildCoveragePassId(props);
    if (passId !== id) continue;
    const geom = f.getGeometry();
    if (!geom) continue;
    const ext = geom.getExtent();
    if (!ext || !isFinite(ext[0])) continue;
    const view = m.getView();
    const viewExtent = view.calculateExtent(m.getSize()!);
    const featureInView =
      ext[0] >= viewExtent[0] && ext[2] <= viewExtent[2] &&
      ext[1] >= viewExtent[1] && ext[3] <= viewExtent[3];
    if (!featureInView) {
      fitExtent(ext as [number, number, number, number], [80, 80, 80, 80]);
    }
    break;
  }
});

function addLayers() {
  const m = map.value;
  if (!m) return;
  m.addLayer(aoiLayer);
  m.addLayer(coverageLayer);
  m.addLayer(coverageHighlightLayer);
  (m as any).on('singleclick', onCoverageClick);
  (m as any).on('pointermove', onCoverageHover);
  m.getViewport()?.addEventListener('mouseleave', onViewportLeave);
  m.on('moveend', onMapMoveEnd);
}

function removeLayers() {
  const m = map.value;
  if (!m) return;
  (m as any).un('singleclick', onCoverageClick);
  (m as any).un('pointermove', onCoverageHover);
  m.getViewport()?.removeEventListener('mouseleave', onViewportLeave);
  m.un('moveend', onMapMoveEnd);
  m.removeLayer(coverageHighlightLayer);
  m.removeLayer(coverageLayer);
  m.removeLayer(aoiLayer);
}

watch(map, (m) => { if (m) addLayers(); }, { immediate: true });
onBeforeUnmount(() => {
  if (coverageRebuildRafId != null && typeof window !== 'undefined') {
    window.cancelAnimationFrame(coverageRebuildRafId);
    coverageRebuildRafId = null;
  }
  removeLayers();
});

watch(mapViewMode, (mode) => {
  const isVisible = mode === 'aoi' && aoiCoverageVisible.value;
  coverageLayer.setVisible(isVisible);
  coverageHighlightLayer.setVisible(isVisible);
  aoiLayer.setVisible(visibleAreas.value.length > 0);
  if (mode === 'aoi') {
    // Bridge swath -> AOI selection where possible; otherwise clear to avoid hiding all AOI features.
    const current = selectedPassId.value;
    if (current && !current.startsWith('aoi-')) {
      const sourcePass = selectedPass.value ?? swathGraphPasses.value.find((p) => p.id === current) ?? null;
      if (!sourcePass) {
        selectedPassId.value = null;
      } else {
        const exactId = `aoi-${sourcePass.name}-${sourcePass.start_time}-${sourcePass.end_time}`;
        const exact = aoiPassesAll.value.find((p) => p.id === exactId);
        if (exact) {
          selectedPassId.value = exact.id;
        } else {
          const srcStart = Date.parse(sourcePass.start_time);
          const srcEnd = Date.parse(sourcePass.end_time);
          if (!Number.isFinite(srcStart) || !Number.isFinite(srcEnd)) {
            selectedPassId.value = null;
          } else {
            const srcMid = (srcStart + srcEnd) / 2;
            let bestId: string | null = null;
            let bestOverlap = -1;
            let bestMidDelta = Number.POSITIVE_INFINITY;
            for (const candidate of aoiPassesAll.value) {
              if (candidate.name.toUpperCase() !== sourcePass.name.toUpperCase()) continue;
              const cStart = Date.parse(candidate.start_time);
              const cEnd = Date.parse(candidate.end_time);
              if (!Number.isFinite(cStart) || !Number.isFinite(cEnd)) continue;
              const overlap = Math.max(0, Math.min(srcEnd, cEnd) - Math.max(srcStart, cStart));
              const cMid = (cStart + cEnd) / 2;
              const midDelta = Math.abs(cMid - srcMid);
              if (overlap > bestOverlap || (overlap === bestOverlap && midDelta < bestMidDelta)) {
                bestId = candidate.id;
                bestOverlap = overlap;
                bestMidDelta = midDelta;
              }
            }
            selectedPassId.value = bestId;
          }
        }
      }
      lastClickCoord = null;
    }
    nextTick(() => focusMostRecentCalculatedAoi(true));
    return;
  }

  popupVisible.value = false;
  setHoveredPass(null);
}, { immediate: true });

watch(aoiCoverageVisible, (visible) => {
  const isVisible = mapViewMode.value === 'aoi' && visible;
  coverageLayer.setVisible(isVisible);
  coverageHighlightLayer.setVisible(isVisible);
  aoiLayer.setVisible(visibleAreas.value.length > 0);
  if (!isVisible) {
    popupVisible.value = false;
    setHoveredPass(null);
  }
}, { immediate: true });

watch(isColorblindFriendly, () => {
  coverageStyleCache.clear();
  coverageLayer.changed();
  coverageHighlightLayer.changed();
});

watch(
  [map, areas],
  ([m]) => {
    if (!m || mapViewMode.value !== 'aoi') return;
    // Covers revisit/load case when AOI is already the persisted active view.
    nextTick(() => focusMostRecentCalculatedAoi(false));
  },
  { immediate: true, deep: true },
);

watch(visibleAreas, (areas, oldAreas) => {
  aoiSource.clear();
  aoiLayer.setVisible(areas.length > 0);
  for (const area of areas) {
    if (!area.aoi_geojson) continue;
    try {
      const features = format.readFeatures(area.aoi_geojson, { featureProjection: 'EPSG:3857' });
      aoiSource.addFeatures(features);
    } catch {
      // ignore malformed GeoJSON
    }
  }
  if (areas.length > (oldAreas?.length ?? 0)) {
    const ext = aoiSource.getExtent();
    if (ext && isFinite(ext[0])) {
      fitExtent(ext as [number, number, number, number], [60, 60, 60, 60]);
    }
  }
}, { immediate: true });

let coverageRebuildRafId: number | null = null;

function rebuildCoverageLayer() {
  coverageSource.clear();
  coverageFeatureMap.clear();
  coverageStyleCache.clear();
  if (mapViewMode.value !== 'aoi') return;

  if (visibleAreas.value.length) {
    const mergedFeatures: Feature<Geometry>[] = [];
    for (const area of visibleAreas.value) {
      if (!area.coverage_geojson) continue;
      try {
        const allFeatures = format.readFeatures(area.coverage_geojson, { featureProjection: 'EPSG:3857' });
        const filtered = filterFeaturesBySatelliteAndTime(allFeatures);
        for (const feature of filtered) annotateCoverageFeature(feature);
        mergedFeatures.push(...filtered);
      } catch {
        // ignore per-area parse errors
      }
    }
    mergedFeatures.sort((a, b) => {
      const aMs = getCoverageSortMs(a);
      const bMs = getCoverageSortMs(b);
      if (aMs === bMs) return 0;
      // Draw distant/late features first so soonest overpasses stay on top.
      return aMs > bMs ? -1 : 1;
    });
    coverageSource.addFeatures(mergedFeatures);
    mergedFeatures.forEach(registerCoverageFeature);
    return;
  }

  if (areas.value.length === 0 && coverageResult.value?.geojson) {
    try {
      const allFeatures = format.readFeatures(coverageResult.value.geojson, { featureProjection: 'EPSG:3857' });
      const filtered = filterFeaturesBySatelliteAndTime(allFeatures);
      for (const feature of filtered) annotateCoverageFeature(feature);
      filtered.sort((a, b) => {
        const aMs = getCoverageSortMs(a);
        const bMs = getCoverageSortMs(b);
        if (aMs === bMs) return 0;
        return aMs > bMs ? -1 : 1;
      });
      coverageSource.addFeatures(filtered);
      filtered.forEach(registerCoverageFeature);
    } catch {
      // silently ignore
    }
  }
}

function scheduleCoverageLayerRebuild(): void {
  if (typeof window === 'undefined') {
    rebuildCoverageLayer();
    return;
  }
  if (coverageRebuildRafId != null) {
    window.cancelAnimationFrame(coverageRebuildRafId);
  }
  coverageRebuildRafId = window.requestAnimationFrame(() => {
    coverageRebuildRafId = null;
    rebuildCoverageLayer();
  });
}

watch(
  [visibleAreas, coverageResult, selectedSatelliteNames, timeRange, metaTimeRange, mapViewMode, otDataSourceFilter, cloudCoverThreshold],
  () => {
    scheduleCoverageLayerRebuild();
  },
  { immediate: true, deep: true, flush: 'post' },
);
</script>
